import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DefineAudience } from './components/DefineAudience';
import {
    DefineAudienceFormFields,
    defineAudienceSchema,
    type DefineAudienceFormData,
} from './validation';
import { buildCompanyAttributeOptions } from '../CycleInformation/companyAttributeOptions';

import type { CycleEvaluationFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationFormNavigator';
import type { UserFilterParams } from '@/types/api';
import type { CycleAudience } from '@/types/cycle-evaluation/CycleAudience';
import type {
    CompanyAttribute as CompanyAttributeType,
    CompanyManager as CompanyManagerType,
} from '@/types/directory/Company';
import type { User } from '@/types/directory/User';
import type { RadioGroupOption } from '@amelio/ui-library';
import type { OptionProps } from '@amelio/ui-library';

import { useUpdateCycleAudience } from '@/api/cycle-evaluation-services/cycle-audience';
import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import {
    useGetUserPermissions,
    useGetCompanyInformation,
    useGetCompanyPeople,
} from '@/api/directory';
import { defineAudienceOnNextAtom } from '@/atoms/cycleEvaluation';
import {
    CycleAudienceType,
    type CycleAudienceTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations, useRegisterPageAction } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import {
    defaultFiltersState,
    useCompanyAttributesFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import { useError } from '@/hooks/utils/useError';
import { showWarningToast } from '@/plugins/Toast';
import { getCompanyAttributeList } from '@/utils/company';
import { transformCriteriaToFilters, transformFiltersToCriteria } from '@/utils/cycleEvaluation';
import { validationMessageKey } from '@/utils/validationMessage';

export const DefineAudiencePage = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const { cycleEvaluationId } = useParams<CycleEvaluationFormNavigatorParams>();
    const [hasUserChangedOption, setHasUserChangedOption] = useState(false);
    const [selectedOptionState, setSelectedOptionState] = useState<CycleAudienceTypeValue | null>(
        null,
    );

    const {
        data: cycleEvaluation,
        isError: cycleEvaluationError,
        isPending: isCycleEvaluationPending,
    } = useGetCycleEvaluation(cycleEvaluationId);

    const { cycleAudience } = cycleEvaluation || {};

    const {
        data: companyInformation,
        isError: companyInformationError,
        isPending: companyInformationPending,
    } = useGetCompanyInformation();

    const {
        data: permissions,
        isLoading: permissionsLoading,
        isError: permissionsError,
    } = useGetUserPermissions();

    const {
        filters,
        selectedCompanyAttributes,
        onSelectCompanyAttributeOption,
        onSelectCompanyAttribute,
        setFilters,
        setSelectedCompanyAttributes,
    } = useCompanyAttributesFilters({
        company: companyInformation,
    });

    const filterParams: UserFilterParams = useMemo(() => {
        const toIds = (items: Array<CompanyAttributeType | CompanyManagerType>) =>
            items.map((i) => i.id).filter((id): id is string => !!id);

        const ifNotEmpty = (items: Array<CompanyAttributeType | CompanyManagerType>) =>
            items.length > 0 ? toIds(items) : undefined;

        return {
            departmentIds: ifNotEmpty(filters.departments),
            jobTitleIds: ifNotEmpty(filters.jobTitles),
            managerIds: ifNotEmpty(filters.managers),
            companyAddressIds: ifNotEmpty(filters.companyAddresses),
            employeeLevelIds: ifNotEmpty(filters.employeeLevels),
            customAttributeIds: ifNotEmpty(filters.customAttributes),
        };
    }, [filters]);

    const {
        data: companyPeopleData,
        isError: companyPeopleError,
        isPending: companyPeopleLoading,
    } = useGetCompanyPeople(
        {
            pageNumber: 1,
            pageSize: 50,
            isActiveOnly: true,
            ...filterParams,
        },
        {
            canViewResultsOfAllDepartments: permissions?.canViewResultsOfAllDepartments,
        },
    );

    const { mutateAsync: updateCycleAudience } = useUpdateCycleAudience();

    useFetchErrorsHandler({
        errors: [
            companyInformationError,
            cycleEvaluationError,
            companyPeopleError,
            permissionsError,
        ],
    });

    const { setValue, trigger, getFieldState } = useForm<DefineAudienceFormData>({
        resolver: zodResolver(defineAudienceSchema),
        defaultValues: {
            [DefineAudienceFormFields.computedEmployees]: [],
        },
    });

    const isLoading =
        companyInformationPending ||
        isCycleEvaluationPending ||
        companyPeopleLoading ||
        permissionsLoading;

    const selectedOption = useMemo(() => {
        if (hasUserChangedOption) {
            return selectedOptionState ?? CycleAudienceType.ENTIRE_COMPANY;
        }

        return cycleAudience?.cycleAudienceType ?? CycleAudienceType.ENTIRE_COMPANY;
    }, [hasUserChangedOption, selectedOptionState, cycleAudience?.cycleAudienceType]);

    const companyAttributes: OptionProps[] = useMemo(
        () => buildCompanyAttributeOptions(companyInformation),
        [companyInformation],
    );

    const computedEmployees: User[] = useMemo(() => {
        return companyPeopleData?.items ?? [];
    }, [companyPeopleData]);

    const options: RadioGroupOption[] = [
        {
            label: t('cycleEvaluationDefineAudience.content.entireCompany.label'),
            value: CycleAudienceType.ENTIRE_COMPANY,
        },
        {
            label: t('cycleEvaluationDefineAudience.content.filterByAttributes.label'),
            value: CycleAudienceType.FILTER_BY_ATTRIBUTES,
        },
    ];

    const handleSetSelectedOption = (value: CycleAudienceTypeValue) => {
        setHasUserChangedOption(true);
        setSelectedOptionState(value);
        setFilters(defaultFiltersState);
    };

    const onNext = useCallback(async () => {
        setValue(DefineAudienceFormFields.computedEmployees, computedEmployees, {
            shouldValidate: false,
        });

        const isValid = await trigger(DefineAudienceFormFields.computedEmployees);

        if (!isValid) {
            const fieldState = getFieldState(DefineAudienceFormFields.computedEmployees);
            const errorMessage = validationMessageKey(fieldState.error?.message);

            showWarningToast({
                message: errorMessage
                    ? t(errorMessage)
                    : t('cycleEvaluationDefineAudience.validation.noAudience'),
            });

            return false;
        }

        try {
            const newAudience: CycleAudience = {
                id: cycleAudience?.id || '',
                cycleEvaluationId: cycleEvaluation?.id || '',
                cycleAudienceType: selectedOption,
                cycleAudienceCriterias: transformFiltersToCriteria({
                    filters,
                    criterias: cycleEvaluation?.cycleAudience?.cycleAudienceCriterias || [],
                    cycleAudienceId: cycleEvaluation?.cycleAudience?.id || '',
                }),
            };

            await updateCycleAudience(newAudience);
        } catch (error) {
            handleError({ error });
            return false;
        }

        return true;
    }, [
        computedEmployees,
        setValue,
        trigger,
        getFieldState,
        t,
        cycleAudience?.id,
        cycleEvaluation?.id,
        cycleEvaluation?.cycleAudience?.cycleAudienceCriterias,
        filters,
        selectedOption,
        updateCycleAudience,
        handleError,
    ]);

    useEffect(() => {
        if (!isCycleEvaluationPending && cycleEvaluation?.cycleAudience?.cycleAudienceCriterias) {
            const companyAttributes = getCompanyAttributeList(companyInformation);

            const { filters: transformedFilters, selectedAttributes } = transformCriteriaToFilters({
                criterias: cycleEvaluation.cycleAudience.cycleAudienceCriterias,
                companyAttributes,
            });

            setFilters(transformedFilters);
            setSelectedCompanyAttributes(selectedAttributes);
        }
    }, [
        isCycleEvaluationPending,
        cycleEvaluation?.cycleAudience?.cycleAudienceCriterias,
        companyInformation,
        setFilters,
        setSelectedCompanyAttributes,
    ]);

    useRegisterPageAction(defineAudienceOnNextAtom, onNext);

    return (
        <DefineAudience
            options={options}
            isLoading={isLoading}
            selectedEmployeesCount={companyPeopleData?.totalCount ?? 0}
            selectedOption={selectedOption}
            companyAttributes={companyAttributes}
            selectedCompanyAttributes={selectedCompanyAttributes}
            filters={filters}
            setSelectedOption={handleSetSelectedOption}
            onSelectCompanyAttribute={onSelectCompanyAttribute}
            onSelectCompanyAttributeOption={onSelectCompanyAttributeOption}
        />
    );
};
